import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import { useAuthStore } from "@/stores/authStore";
import { openDeclineFor, type CalendarPlanSlot } from "@/types/contentCalendar.type";
import type { BriefComment, ProductionBrief } from "@/types/productionBrief.type";
import { conceptVersionsKey, useConceptVersions } from "../api/useConceptVersions";
import { slotBriefKey, useBriefRevisions } from "../api/useProductionBrief";
import { postThreadsKey, usePostThreads } from "./commentApi";
import {
  commentsClosed,
  groupThreads,
  laterOf,
  NEW_COMMENT_VISIBILITY,
  threadsFromApi,
  type CommentEntry,
  type CommentSource,
  type CommentThreadData,
} from "./commentModel";
import { useCommentActions } from "./useCommentActions";

/** Where a new comment is pinned, and whom it names. */
export interface PostOptions {
  /** A part of the page ("hook", or "shot" with key "3"); none is the whole post. */
  anchor?: { part: string; key?: string | null; label?: string } | null;
  /** A reply: the thread's id (its first comment). */
  parentId?: string | null;
  /** The thread's concept version id or brief id, for a reply. */
  targetId?: string | null;
  mentions?: { kind: "user" | "client"; id: string; name: string }[];
}

/** A post written on this page: on screen at once, and kept if it fails. */
type LocalPost = {
  localId: string;
  slotId: string;
  source: CommentSource;
  /** The concept version id or the brief id it goes to. */
  targetId: string;
  version: number;
  body: string;
  parentId: string | null;
  anchor: PostOptions["anchor"];
  mentions: NonNullable<PostOptions["mentions"]>;
  createdAt: string;
  state: "sending" | "failed";
};

/** Concept versions only exist once a concept has been sent at least once. */
function mayHaveConceptVersions(slot: CalendarPlanSlot): boolean {
  return (
    (slot.reviewDecisions?.length ?? 0) > 0 ||
    !!openDeclineFor(slot) ||
    ["SIGNED_OFF", "IN_CLIENT_REVIEW", "CLIENT_APPROVED"].includes(slot.status)
  );
}

/** The clock comment times read against; a minute is fine enough. */
function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

function errorMessage(err: unknown): string {
  if (isAxiosError(err)) return (err.response?.data as { message?: string })?.message ?? "Please try again.";
  return "Please try again.";
}

let localSeq = 0;

/**
 * The team post page's comments: every thread on the post (backend #128) with
 * its replies, marker, done state and M7's change (#125), plus what this page
 * is posting. A post or reply shows at once and stays, with Try again, if it
 * fails; edits, deletes, done and M7's changes are optimistic in the cache.
 */
export function usePostComments({
  slot,
  planId,
  brief,
  canEdit,
  enabled,
}: {
  slot: CalendarPlanSlot | null;
  planId: string;
  brief: ProductionBrief | null;
  /** Kept for callers; the threads read carries the brief's comments now. */
  briefComments?: BriefComment[];
  canEdit: boolean;
  /** The team's page; the client's side keeps its own. */
  enabled: boolean;
}) {
  const qc = useQueryClient();
  const slotId = slot?.id ?? null;
  const user = useAuthStore((s) => s.user);
  const authorName = user?.name?.trim() || "You";
  const userId = user?.id ?? null;
  const threadsQuery = usePostThreads(enabled ? slotId : null);
  const conceptQuery = useConceptVersions(enabled && slot && mayHaveConceptVersions(slot) ? slot.id : null);
  const actions = useCommentActions({ slotId, planId });
  const [pending, setPending] = useState<LocalPost[]>([]);
  const now = useMinuteClock();

  // No feedback after approval: the idea and the brief both approved closes
  // the composer. The brief's approval date is in its revisions (the same
  // query the post page reads, so it is one request).
  const closed = !!slot && commentsClosed(slot.status, brief?.status);
  const revisions = useBriefRevisions(brief?.id ?? null, enabled && closed);
  const closedAt = useMemo(() => {
    if (!closed) return null;
    const conceptDecision = slot?.reviewDecisions?.[0];
    const conceptAt = conceptDecision?.decision === "APPROVED" ? conceptDecision.decidedAt : null;
    const briefAts = (revisions.data?.revisions ?? []).flatMap((entry) =>
      (entry.decisionsInRounds?.length ? entry.decisionsInRounds : entry.decisionInRound ? [entry.decisionInRound] : [])
        .filter((decision) => decision.decision === "APPROVED")
        .map((decision) => decision.decidedAt ?? null),
    );
    return laterOf(conceptAt, ...briefAts);
  }, [closed, slot?.reviewDecisions, revisions.data?.revisions]);
  const closedBy: "client" | "team" =
    slot?.approvalSource === "TEAM" || brief?.acceptanceSource === "TEAM" ? "team" : "client";

  const versions = useMemo(() => conceptQuery.data?.versions ?? [], [conceptQuery.data?.versions]);
  const latestShared = useMemo(
    () => [...versions].filter((v) => !!v.sharedAt).sort((a, b) => b.version - a.version)[0] ?? null,
    [versions],
  );

  // The team comments on the latest sent version while the concept is still
  // open, and never after approval (the API says the same in `open`).
  const apiOpen = threadsQuery.data?.open;
  const conceptOpen =
    !!slot && ["PLANNED", "SIGNED_OFF", "IN_CLIENT_REVIEW"].includes(slot.status) &&
    latestShared?.decision?.decision !== "APPROVED" &&
    apiOpen?.concept !== false;
  const conceptTarget = canEdit && conceptOpen && latestShared ? latestShared : null;
  const briefTarget = canEdit && brief && apiOpen?.brief !== false ? brief : null;

  const threads: CommentThreadData[] = useMemo(() => {
    if (!enabled || !slotId) return [];
    const mine = pending.filter((p) => p.slotId === slotId);
    const entry = (p: LocalPost): CommentEntry => ({
      id: p.localId,
      authorType: "TEAM",
      authorName,
      body: p.body,
      createdAt: p.createdAt,
      authorUserId: userId,
      mentions: p.mentions,
      sendState: p.state,
    });
    const server = threadsFromApi(threadsQuery.data?.threads ?? []).map((thread) => {
      const replies = mine.filter((p) => p.parentId && `${p.source}:${p.parentId}` === thread.id);
      return replies.length ? { ...thread, replies: [...(thread.replies ?? []), ...replies.map(entry)] } : thread;
    });
    const roots: CommentThreadData[] = mine
      .filter((p) => !p.parentId)
      .map((p) => ({
        id: `local:${p.localId}`,
        source: p.source,
        version: p.version,
        comment: entry(p),
        anchor: p.anchor?.label ? { part: p.anchor.part, label: p.anchor.label } : null,
        replies: [],
        targetId: p.targetId,
      }));
    return [...server, ...roots];
  }, [enabled, slotId, pending, threadsQuery.data?.threads, authorName, userId]);

  const groups = useMemo(() => groupThreads(threads), [threads]);

  const setState = (localId: string, state: LocalPost["state"] | null) =>
    setPending((list) =>
      state === null
        ? list.filter((p) => p.localId !== localId)
        : list.map((p) => (p.localId === localId ? { ...p, state } : p)),
    );

  const send = async (post: LocalPost) => {
    setState(post.localId, "sending");
    const thread = {
      ...(post.parentId ? { parentId: post.parentId } : {}),
      ...(post.anchor && !post.parentId ? { anchor: { part: post.anchor.part, key: post.anchor.key ?? null } } : {}),
      ...(post.mentions.length ? { mentions: post.mentions.map(({ kind, id }) => ({ kind, id })) } : {}),
    };
    try {
      if (post.source === "concept") {
        await axiosInstance.post(`/content-calendar/plan-slots/${post.slotId}/concept-versions/${post.targetId}/comments`, {
          body: post.body,
          visibility: NEW_COMMENT_VISIBILITY,
          ...thread,
        });
        void qc.invalidateQueries({ queryKey: conceptVersionsKey(post.slotId) });
      } else {
        await axiosInstance.post(`/content-calendar/briefs/${post.targetId}/comments`, { body: post.body, ...thread });
        void qc.invalidateQueries({ queryKey: slotBriefKey(post.slotId) });
      }
      // Held until the thread has the server's copy, so it never blinks out.
      await qc.invalidateQueries({ queryKey: postThreadsKey(post.slotId) });
      setState(post.localId, null);
    } catch (err) {
      queue.add({ title: "Could not post that", description: errorMessage(err), variant: "error" });
      // The post stays here with Try again.
      setState(post.localId, "failed");
    }
  };

  /** Every comment is for everyone: concept comments post SHARED. */
  const post = (source: CommentSource, body: string, options: PostOptions = {}) => {
    if (!slotId || closed) return;
    const reply = !!options.parentId;
    const target = source === "concept" ? conceptTarget : briefTarget;
    const targetId = reply ? options.targetId : target?.id;
    if (!targetId || (!reply && !target)) return;
    localSeq += 1;
    const next: LocalPost = {
      localId: `${Date.now().toString(36)}-${localSeq}`,
      slotId,
      source,
      targetId,
      version: target?.version ?? 1,
      body,
      parentId: options.parentId ?? null,
      anchor: options.anchor ?? null,
      mentions: options.mentions ?? [],
      createdAt: new Date().toISOString(),
      state: "sending",
    };
    setPending((list) => [...list, next]);
    void send(next);
  };

  const retry = (threadId: string) => {
    const post = pending.find((p) => `local:${p.localId}` === threadId || p.localId === threadId);
    if (post) void send(post);
  };

  return {
    threads,
    /** Only people who can edit get the composer. */
    canComment: canEdit,
    open: groups.open,
    done: groups.done,
    count: groups.open.length,
    /** The first read is out: the rail shows thread skeletons. */
    loading: enabled && !!slotId && threadsQuery.isPending,
    now,
    authorName,
    /** Your own comments get Edit and Delete. */
    userId,
    conceptTarget,
    briefTarget,
    hasSharedConcept: !!latestShared,
    conceptApproved: latestShared?.decision?.decision === "APPROVED" || slot?.status === "CLIENT_APPROVED",
    /** The idea and the brief are approved: the composer is one closed line. */
    closed,
    /** When the later of the two approvals happened, when the API says. */
    closedAt,
    closedBy,
    post,
    retry,
    actions,
  };
}

export type PostCommentsData = ReturnType<typeof usePostComments>;
